/**
 * ListaChecklist - Lista de itens do checklist com filtros por tipo
 * Padrão: Todos os itens | Categorias | Especificações | Produtos | Serviços
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ClipboardList, Plus, FolderOpen } from 'lucide-react';
import CategoriaChecklist from './CategoriaChecklist';
import { agruparPorCategoria } from './constantes';

const FILTER_TABS = [
  { key: 'todos', label: 'Todos os itens' },
  { key: 'categorias', label: 'Categorias' },
  { key: 'especificacoes', label: 'Especificações' },
  { key: 'produtos', label: 'Produtos' },
  { key: 'servicos', label: 'Serviços' },
];

const ListaChecklist = ({
  checklistItems,
  vesselModel,
  expandedCategories,
  fornecedores,
  podeVerCustos,
  onToggleCategory,
  onUpdateItem,
  onRemoveItem,
  onDragStartItem,
  onDragOverItem,
  onDragLeaveItem,
  onDropItem,
  dragOverIndex,
  onOpenAddProduto,
  onUpdateItemCusto,
  onRemoveItemCusto,
  onFornecedoresUpdate,
  onOpenAddItem,
  onOpenImport,
  projeto,
  embarcacaoIndex,
  onOpenSolicitarCompra
}) => {
  const [activeFilter, setActiveFilter] = useState('todos');
  const safeItems = Array.isArray(checklistItems) ? checklistItems : [];

  // Contar itens por tipo
  const counts = useMemo(() => ({
    especificacoes: safeItems.filter(i => i?.tipo_item === 'especificacao').length,
    produtos: safeItems.filter(i => i?.tipo_item === 'produto' || !i?.tipo_item).length,
    servicos: safeItems.filter(i => i?.tipo_item === 'servico').length,
  }), [safeItems]);

  // Filtrar itens por tipo
  const filteredItems = useMemo(() => {
    if (activeFilter === 'especificacoes') return safeItems.filter(i => i?.tipo_item === 'especificacao');
    if (activeFilter === 'produtos') return safeItems.filter(i => i?.tipo_item === 'produto' || !i?.tipo_item);
    if (activeFilter === 'servicos') return safeItems.filter(i => i?.tipo_item === 'servico');
    return safeItems;
  }, [safeItems, activeFilter]);

  const categorias = agruparPorCategoria(filteredItems);

  if (safeItems.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500">Nenhum item no checklist</p>
          <p className="text-sm text-gray-400 mt-1">
            {vesselModel ? 'O modelo não possui checklist definido' : 'Selecione um modelo de embarcação'}
          </p>
          <div className="flex justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={onOpenAddItem}>
              <Plus className="h-4 w-4 mr-1" />
              Adicionar Item
            </Button>
            <Button variant="outline" size="sm" onClick={onOpenImport}>
              <FolderOpen className="h-4 w-4 mr-1" />
              Importar Ficha
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="lista-checklist">
      {/* Filtros */}
      <div className="flex flex-wrap gap-1 mb-3">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
              activeFilter === tab.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            {tab.key === 'especificacoes' && <span className="ml-1 opacity-75">({counts.especificacoes})</span>}
            {tab.key === 'produtos' && <span className="ml-1 opacity-75">({counts.produtos})</span>}
            {tab.key === 'servicos' && <span className="ml-1 opacity-75">({counts.servicos})</span>}
          </button>
        ))}
      </div>

      {/* Ações */}
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={onOpenImport}>
          <FolderOpen className="h-4 w-4 mr-1" />
          Importar Ficha
        </Button>
        <Button variant="outline" size="sm" onClick={onOpenAddItem}>
          <Plus className="h-4 w-4 mr-1" />
          Adicionar Item
        </Button>
      </div>

      {/* Resumo do filtro ativo */}
      {activeFilter !== 'todos' && activeFilter !== 'categorias' && (
        <div className="text-sm text-gray-500">
          Exibindo {filteredItems.length} de {safeItems.length} itens
        </div>
      )}

      {/* Lista vazia para filtro */}
      {filteredItems.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-gray-400 text-sm">Nenhum item nesta categoria</p>
          </CardContent>
        </Card>
      )}

      {/* Categorias */}
      {categorias.map(categoria => {
        const itensCategoria = filteredItems.filter(
          item => (item?.categoria || 'Geral') === categoria
        );

        if (itensCategoria.length === 0) return null;

        return (
          <CategoriaChecklist
            key={categoria}
            categoria={categoria}
            itensCategoria={itensCategoria}
            checklistItems={safeItems}
            isExpanded={expandedCategories[categoria] !== false}
            onToggle={onToggleCategory}
            fornecedores={fornecedores}
            podeVerCustos={podeVerCustos}
            onUpdateItem={onUpdateItem}
            onRemoveItem={onRemoveItem}
            onDragStartItem={onDragStartItem}
            onDragOverItem={onDragOverItem}
            onDragLeaveItem={onDragLeaveItem}
            onDropItem={onDropItem}
            dragOverIndex={dragOverIndex}
            onOpenAddProduto={onOpenAddProduto}
            onUpdateItemCusto={onUpdateItemCusto}
            onRemoveItemCusto={onRemoveItemCusto}
            onFornecedoresUpdate={onFornecedoresUpdate}
            projeto={projeto}
            embarcacaoIndex={embarcacaoIndex}
            onOpenSolicitarCompra={onOpenSolicitarCompra}
          />
        );
      })}
    </div>
  );
};

export default ListaChecklist;
